import React, { useState, useCallback, useEffect } from 'react';
import { Loader2, RotateCcw, Save, AlertTriangle, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const LANG_FIELDS = [
  { key: 'fr', label: 'Français' },
  { key: 'en', label: 'English' },
  { key: 'de', label: 'Deutsch' },
  { key: 'lb', label: 'Lëtzebuergesch' },
];

const DEFAULT_CONFIG = {
  provider: 'navitia',
  footer_labels: {
    fr: 'Données : SNCF Open Data • Navitia • GTFS-RT',
    en: 'Data: SNCF Open Data • Navitia • GTFS-RT',
    de: 'Daten: SNCF Open Data • Navitia • GTFS-RT',
    lb: 'Donnéeën: SNCF Open Data • Navitia • GTFS-RT',
  },
};

const pivEnvBadge = (url) => {
  if (!url) return null;
  if (url.includes('preprod')) return { label: 'Préprod',    cls: 'bg-orange-100 text-orange-800 border-orange-200' };
  if (url.includes('recette')) return { label: 'Recette',    cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' };
  if (url.includes('prod'))    return { label: 'Production', cls: 'bg-red-100 text-red-800 border-red-200' };
  return null;
};

const ApiTab = ({ password, username }) => {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [pivStatus, setPivStatus]   = useState(null);
  const [pivTesting, setPivTesting] = useState(false);
  const [apiStatus, setApiStatus]   = useState(null);
  const [apiTesting, setApiTesting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setFeedback(null);
    try {
      const r = await fetch(`${API}/api/admin/api-config`, {
        headers: { 'X-Admin-Password': password },
      });
      if (!r.ok) throw new Error(r.status);
      const d = await r.json();
      setData({
        provider:      d.provider ?? 'navitia',
        footer_labels: { ...DEFAULT_CONFIG.footer_labels, ...(d.footer_labels ?? {}) },
        piv_env:       d.piv_env ?? { auth_url: null, base_url: null },
      });
    } catch {
      setFeedback({ ok: false, msg: 'Chargement échoué' });
    } finally {
      setLoading(false);
    }
  }, [password]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const r = await fetch(`${API}/api/admin/api-config`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Password': password },
        body:    JSON.stringify(data),
      });
      if (!r.ok) throw new Error(r.status);
      setFeedback({ ok: true, msg: 'Enregistré !' });
    } catch {
      setFeedback({ ok: false, msg: 'Enregistrement échoué' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestPiv = async () => {
    setPivTesting(true);
    setPivStatus(null);
    try {
      const r = await fetch(`${API}/api/piv-status`, {
        headers: { 'X-Admin-Username': username, 'X-Admin-Password': password },
      });
      const d = await r.json();
      setPivStatus(d);
    } catch (e) {
      setPivStatus({ ok: false, error: e.message });
    } finally {
      setPivTesting(false);
    }
  };

  const handleTestPivApi = async () => {
    setApiTesting(true);
    setApiStatus(null);
    try {
      const r = await fetch(`${API}/api/piv-api-test`, {
        headers: { 'X-Admin-Password': password },
      });
      const d = await r.json();
      setApiStatus(d);
    } catch (e) {
      setApiStatus({ ok: false, error: e.message, endpoint: '' });
    } finally {
      setApiTesting(false);
    }
  };

  const setProvider = (v) => { setData(prev => ({ ...prev, provider: v })); setFeedback(null); setPivStatus(null); setApiStatus(null); };
  const setLabel    = (lang, v) => {
    setData(prev => ({ ...prev, footer_labels: { ...prev.footer_labels, [lang]: v } }));
    setFeedback(null);
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">

      {/* En-tête */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">API</div>
          <h2 className="text-xl font-bold text-slate-900">Configuration API &amp; pied de page</h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 text-slate-600 text-sm hover:bg-slate-50 disabled:opacity-50 transition"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
            Charger
          </button>
          <button
            onClick={save}
            disabled={saving || !data}
            className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-ter-blue text-white text-sm font-bold hover:brightness-95 disabled:opacity-50 transition"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Enregistrer
          </button>
        </div>
      </div>

      {/* Feedback */}
      {feedback && (
        <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg mb-6 border ${
          feedback.ok
            ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
            : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          {feedback.ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
          {feedback.msg}
        </div>
      )}

      {/* Spinner initial */}
      {loading && !data && (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      )}

      {data && (
        <div className="space-y-8">

          {/* Source de données */}
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-3">Source de données</div>
            <div className="space-y-2">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="provider"
                  value="navitia"
                  checked={data.provider === 'navitia'}
                  onChange={() => setProvider('navitia')}
                  className="accent-ter-blue"
                />
                <span className="text-sm text-slate-800">Données : SNCF Open Data • Navitia • GTFS-RT</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="provider"
                  value="piv"
                  checked={data.provider === 'piv'}
                  onChange={() => setProvider('piv')}
                  className="accent-ter-blue"
                />
                <span className="text-sm text-slate-800">Données : SNCF PIV</span>
              </label>
            </div>

            {data.provider === 'piv' && (data.piv_env?.auth_url || data.piv_env?.base_url) && (
              <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 space-y-1.5 text-[11px]">
                {[
                  { label: 'Auth', url: data.piv_env?.auth_url },
                  { label: 'API ', url: data.piv_env?.base_url },
                ].filter(row => row.url).map(({ label, url }) => {
                  const badge = pivEnvBadge(url);
                  return (
                    <div key={label} className="flex items-center gap-2 min-w-0">
                      <span className="text-slate-500 font-mono shrink-0">🔗 {label} :</span>
                      <span className="font-mono text-slate-700 truncate flex-1">{url}</span>
                      {badge && (
                        <span className={`shrink-0 px-1.5 py-0.5 rounded border text-[10px] font-bold ${badge.cls}`}>{badge.label}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {data.provider === 'piv' && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-3">Statut de la connexion PIV</div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={handleTestPiv}
                    disabled={pivTesting || apiTesting}
                    className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-ter-blue text-white text-sm font-bold hover:brightness-95 disabled:opacity-50 transition"
                  >
                    {pivTesting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    {pivTesting ? 'Test en cours…' : 'Tester la connexion PIV'}
                  </button>
                  <button
                    onClick={handleTestPivApi}
                    disabled={apiTesting || pivTesting}
                    className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-ter-blue text-white text-sm font-bold hover:brightness-95 disabled:opacity-50 transition"
                  >
                    {apiTesting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    {apiTesting ? 'Test en cours…' : "Tester l'API PIV"}
                  </button>
                </div>
                {pivStatus && (
                  <div className={`mt-3 text-sm px-3 py-2 rounded-lg border ${
                    pivStatus.ok
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                      : 'bg-red-50 border-red-200 text-red-800'
                  }`}>
                    {pivStatus.ok ? (
                      <>
                        <div className="font-semibold">✅ Connexion établie — environnement : {pivStatus.environment}</div>
                        <div className="text-xs mt-0.5 font-mono text-emerald-700">Token : {pivStatus.token_preview}</div>
                      </>
                    ) : (
                      <div>❌ Connexion échouée — {pivStatus.error}</div>
                    )}
                  </div>
                )}
                {apiStatus && (
                  <div className={`mt-3 text-sm px-3 py-2 rounded-lg border ${
                    apiStatus.ok
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                      : 'bg-red-50 border-red-200 text-red-800'
                  }`}>
                    <p className="font-semibold mb-1">
                      {apiStatus.ok ? '✅ Tous les endpoints accessibles' : '❌ Certains endpoints inaccessibles'}
                      {apiStatus.environment && <span className="ml-2 text-xs font-normal opacity-70">({apiStatus.environment})</span>}
                    </p>
                    {apiStatus.endpoints ? (
                      Object.values(apiStatus.endpoints).map((ep, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm mt-1">
                          <span>{ep.ok ? '✅' : '❌'}</span>
                          <span className="font-mono text-xs">{ep.label}</span>
                          <span className={ep.ok ? 'text-green-600' : 'text-red-600'}>
                            HTTP {ep.http_code}
                          </span>
                        </div>
                      ))
                    ) : (
                      apiStatus.error && <p className="text-xs mt-0.5">Erreur : {apiStatus.error}</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Texte du pied de page */}
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-3">Texte du pied de page</div>
            <div className="space-y-3">
              {LANG_FIELDS.map(({ key, label }) => (
                <div key={key}>
                  <label className="block text-[11px] font-bold tracking-wider text-slate-500 mb-1">{label}</label>
                  <input
                    type="text"
                    value={data.footer_labels[key] ?? ''}
                    onChange={e => setLabel(key, e.target.value)}
                    className="w-full h-9 px-3 rounded-md border border-slate-300 focus:border-ter-blue focus:ring-1 focus:ring-ter-blue outline-none text-sm"
                  />
                </div>
              ))}
            </div>
          </div>

        </div>
      )}
    </div>
  );
};

export default ApiTab;
